import React, { useEffect, useMemo, useState } from 'react';
import { Flex, useDisclosure } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';

import { FilterPill } from './components/FilterPill';
import type { FacetClickSource, FilterFacet } from './Filter.types';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { FiltersDrawer } from './FiltersDrawer';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { MobileFiltersTrigger } from './components/MobileFiltersTrigger';
import { useDesignGalleryFiltersContext } from '../../DesignGalleryFiltersProvider';

interface FiltersProps {
  facets: FilterFacet[];
}

const isValidFacetSource = (source: string | undefined): source is FacetClickSource => !!source && ['mobileDrawer', 'desktopDropdown'].includes(source);

export const Filters = React.memo((props: FiltersProps) => {
  const { tq } = useTranslation('stationery');
  const { facets } = props;

  const [defaultActiveDrawerIndex, setDefaultActiveDrawerIndex] = useState(0);

  const isMobileOrTablet = useIsMobileScreen();
  const { isOpen: isDrawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();

  useEffect(() => {
    if (!isMobileOrTablet) {
      closeDrawer();
    }
  }, [isMobileOrTablet, closeDrawer]);

  const { buttonInteracted } = useCardTelemetry();
  const { setActiveFilter } = useDesignGalleryFiltersContext();
  const handleOnFilterChange = useEventCallback<React.ChangeEventHandler<HTMLInputElement>>(evt => {
    const { target } = evt;

    if (!target.disabled && target.dataset.itemId && isValidFacetSource(target.dataset.source)) {
      buttonInteracted({ name: 'designsGallery_filterFacetClicked', facet: 'format', source: target.dataset.source, code: target.dataset.itemId, isChecked: target.checked });
      setActiveFilter(target.dataset.itemId, target.checked);
    }
  });

  const filteredFacets = useMemo(() => {
    return facets.filter(facet => facet.options.filter(option => option.matchingItemCount > 0).length > 1);
  }, [facets]);

  const handleOnTriggerFilterDrawer = useEventCallback((groupCode?: string) => {
    if (groupCode) {
      const targetFacetIndex = filteredFacets.findIndex(facet => facet.groupCode === groupCode);
      setDefaultActiveDrawerIndex(targetFacetIndex !== -1 ? targetFacetIndex : 0);
    } else {
      setDefaultActiveDrawerIndex(-1);
    }

    openDrawer();
  });

  if (filteredFacets.length === 0) {
    return null;
  }

  return (
    <Flex
      flexWrap="nowrap"
      gap={4}
    >
      {isMobileOrTablet ? (
        <MobileFiltersTrigger
          label={tq`Filters`}
          onClick={() => {
            handleOnTriggerFilterDrawer();
          }}
        />
      ) : (
        filteredFacets.map(facet => (
          <FilterPill
            key={facet.groupCode}
            interaction={isMobileOrTablet ? 'drawer' : 'popover'}
            facet={facet}
            onFilterChange={handleOnFilterChange}
            onTriggerFilterDrawer={handleOnTriggerFilterDrawer}
          />
        ))
      )}

      <FiltersDrawer
        isOpen={isDrawerOpen}
        onChange={handleOnFilterChange}
        defaultIndex={defaultActiveDrawerIndex}
        onClose={closeDrawer}
        filterFacets={filteredFacets}
      />
    </Flex>
  );
});
